import { useMemo, useCallback } from "react";

import Sentry from "@similarweb/sentry-logger";

import { apiHelper } from "app/common/services/apiHelper";
import { WebsitePerformanceHeader } from "app/pages/website-analysis/components/WebsitePerformanceHeader";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useRetailIntelligenceUpsell } from "app/services/sitesService/hooks/useRetailIntelligenceUpsell";
import { useSiteInfo } from "app/services/sitesService/hooks/useSiteInfo";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";
import type { ISiteInfoItem, ISiteInfoParams } from "app/services/sitesService/types";
import { SwTrack } from "app/services/SwTrack";

const DISPLAY_APPS = 3;

interface IApp {
    id: string;
    name?: string;
    title?: string;
    icon?: string;
    appId?: string;
    appStore?: string;
}

interface IAppInfo extends IApp {
    appId: string;
    appStore: string;
}

export enum EPrivacyStatus {
    None = "None",
    Private = "Private",
    Public = "Public",
}

export const WebsiteHeaderReact = () => {
    const params = proNavigator.getParams();
    const { key }: { key?: string } = params;
    const apiParams = useMemo(() => {
        return apiHelper.transformParamsForAPI(params) as ISiteInfoParams;
    }, [params]);

    const onError = useCallback(
        (error: Error) => {
            Sentry.errorTracker({
                message: "WebsiteHeaderReact: Error fetching siteInfo",
                e: error,
                level: "error",
                params: {
                    extra: {
                        params: apiParams,
                    },
                },
            });
        },
        [apiParams],
    );

    const domain = apiParams.key as string;
    const { data: siteInfoData, isLoading } = useSiteInfo(apiParams, undefined, { onError });
    const { data: retailIntelligenceUpsell } = useRetailIntelligenceUpsell(domain, {
        enabled: !!domain && !isLoading,
    });
    const retailIntelligenceUpsellCtaHref =
        retailIntelligenceUpsell?.visible && retailIntelligenceUpsell.ctaHref
            ? retailIntelligenceUpsell.ctaHref
            : undefined;

    const data: Partial<ISiteInfoItem> = useMemo(() => {
        if (!siteInfoData || !key) {
            return {};
        }
        const siteData = getSiteDataFromResponse(siteInfoData, key);
        return siteData || {};
    }, [siteInfoData, key]);

    const iosApps = data.relatedApps ? (data.relatedApps["apps_1"] as IApp[]) || [] : [];
    const googleApps = data.relatedApps ? (data.relatedApps["apps_0"] as IApp[]) || [] : [];

    const getIosApps = (): { apps: IAppInfo[]; showMore: boolean; showBack: boolean } => {
        const apps: IAppInfo[] = iosApps.map((app) => {
            app.appId = `1_${app.id}`;
            app.appStore = "apple";
            return app as IAppInfo;
        });

        return {
            apps: apps,
            showMore: apps.length > DISPLAY_APPS,
            showBack: false,
        };
    };

    const getGoogleApps = (): { apps: IAppInfo[]; showMore: boolean; showBack: boolean } => {
        const apps: IAppInfo[] = googleApps.map((app) => {
            app.appId = `0_${app.id}`;
            app.appStore = "google";

            return app as IAppInfo;
        });

        return {
            apps: apps,
            showMore: apps.length > DISPLAY_APPS,
            showBack: false,
        };
    };

    type RelatedAppsType = { ios?: ReturnType<typeof getIosApps>; google?: ReturnType<typeof getGoogleApps> };
    let relatedApps: RelatedAppsType = {};
    if (!data.isVirtual && data.relatedApps) {
        relatedApps = {
            ios: getIosApps(),
            google: getGoogleApps(),
        };
        relatedApps.ios.showMore = relatedApps.ios.apps.length > DISPLAY_APPS;
        relatedApps.google.showMore = relatedApps.google.apps.length > DISPLAY_APPS;
    }

    const showMore = function (store: string) {
        SwTrack.all.trackEvent("Internal Link", "click", "Related Apps/" + store + "/More");
        const storeApps = store === "ios" ? relatedApps.ios : store === "google" ? relatedApps.google : undefined;
        if (storeApps) {
            storeApps.apps = storeApps.apps.slice(DISPLAY_APPS, storeApps.apps.length);

            if (storeApps.apps.length <= DISPLAY_APPS) {
                storeApps.showMore = false;
                storeApps.showBack = true;
            }
        }
    };

    const back = function (store: string) {
        if (store === "ios") {
            relatedApps.ios = getIosApps();
        }
        if (store === "google") {
            relatedApps.google = getGoogleApps();
        }
    };

    return (
        <WebsitePerformanceHeader
            loading={isLoading}
            domain={domain}
            description={data.description}
            favIconUrl={data.icon}
            websiteHeaderImage={data.imageLarge}
            privacyStatus={data.privacyStatus}
            hasGAToken={data.hasGaToken}
            relatedApps={relatedApps}
            relatedAppsShowBack={back}
            relatedAppsShowMore={showMore}
            retailIntelligenceUpsellCtaHref={retailIntelligenceUpsellCtaHref}
        />
    );
};
